import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { CrackingRisk, ElementType, SimulationElement, SPANISH_STANDARD_MIXTURES } from '@cosmos/shared';

/**
 * Grid-view card for a single-element simulation — same fields as
 * SimulationHistoryListComponent's table row (date, element, mixture, peak
 * temp, cracking risk, favourite), laid out as a card. Sibling to
 * SimulationRowComponent, which renders the same data as a list row.
 */
@Component({
  selector: 'cosmos-simulation-card',
  imports: [DatePipe, DecimalPipe, TranslatePipe, ButtonModule, CardModule, TagModule, TooltipModule],
  templateUrl: './simulation-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .simulation-card ::ng-deep .p-card {
      box-shadow: var(--p-card-shadow);
      transition: box-shadow 150ms ease;
      cursor: pointer;
    }
    .simulation-card:hover ::ng-deep .p-card {
      box-shadow: var(--p-card-shadow), 0 0 0 1px var(--p-surface-300);
    }
  `,
})
export class SimulationCardComponent {
  readonly simulation = input.required<Partial<SimulationElement>>();
  readonly favouritePending = input<boolean>(false);

  readonly open = output<void>();
  readonly favouriteToggled = output<void>();

  protected readonly riskSeverity = computed((): 'success' | 'warn' | 'danger' | undefined => {
    switch (this.simulation().results?.overallCrackingRisk) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warn';
      case 'HIGH': return 'danger';
      default: return undefined;
    }
  });

  protected elementLabel(type: ElementType | undefined): string {
    return type ? `simulator.element-types.${type.toLowerCase()}` : '';
  }

  protected riskLabel(risk: CrackingRisk | undefined): string {
    return risk ? `simulator.history.risk.${risk.toLowerCase()}` : '';
  }

  /** Human mixture name for standard mixtures; empty string → template falls back to "Custom". */
  protected mixtureLabel(s: Partial<SimulationElement>): string {
    if (s.mixtureKey) return SPANISH_STANDARD_MIXTURES[s.mixtureKey]?.name ?? s.mixtureKey;
    return '';
  }
}
